import { useState, useRef, useEffect } from 'react';

/**
 * Smoothly reveals streaming text character-by-character using requestAnimationFrame.
 *
 * This is the technique behind the fluid feel in ChatGPT / Claude:
 * tokens arrive from SSE in irregular bursts, but the UI reveals them
 * at a steady, adaptive rate synced to the display refresh.
 *
 * @param {string}  targetText  – Full text received from the stream so far
 * @param {boolean} isStreaming – Whether the stream is still active
 * @returns {string} The progressively-revealed text to render
 */
export function useTokenAnimator(targetText, isStreaming) {
  const target = targetText || '';

  const [displayedText, setDisplayedText] = useState(target);
  const cursorRef = useRef(target.length);
  const rafRef = useRef(null);
  const lastTimeRef = useRef(0);
  const carryRef = useRef(0);
  const targetRef = useRef(target);
  const streamingRef = useRef(isStreaming);

  targetRef.current = target;
  streamingRef.current = isStreaming;

  useEffect(() => {
    if (!isStreaming) {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      const t = targetRef.current;
      cursorRef.current = t.length;
      carryRef.current = 0;
      setDisplayedText(t);
      return;
    }

    const tick = (timestamp) => {
      const t = targetRef.current;
      const cursor = cursorRef.current;

      if (cursor > t.length) {
        cursorRef.current = t.length;
        carryRef.current = 0;
        setDisplayedText(t);
        lastTimeRef.current = timestamp;
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const buffered = t.length - cursor;

      if (buffered > 0) {
        const lastTs = lastTimeRef.current || timestamp;
        const dtMs = Math.min(100, Math.max(0, timestamp - lastTs));
        lastTimeRef.current = timestamp;

        // Queue interpolation strategy:
        // - keep a minimum reveal speed to avoid visible pauses between bursts
        // - increase speed as backlog grows so we catch up quickly after chunks
        // Keep cadence steady and low-variance:
        // - small baseline keeps text moving between sparse chunks
        // - gentle backlog boost prevents long catch-up delays
        // - hard per-frame cap avoids multi-word "blurts"
        const baseCharsPerSec = 26;
        const burstBoostCharsPerSec = Math.min(90, buffered * 0.9);
        const charsThisFrameFloat = ((baseCharsPerSec + burstBoostCharsPerSec) * dtMs) / 1000 + carryRef.current;
        const charsThisFrame = Math.max(1, Math.min(3, Math.floor(charsThisFrameFloat)));
        carryRef.current = charsThisFrameFloat - charsThisFrame;

        const nextCursor = Math.min(cursor + charsThisFrame, t.length);
        cursorRef.current = nextCursor;
        setDisplayedText(t.slice(0, nextCursor));
      } else {
        // Keep timing stable while waiting for next incoming burst.
        lastTimeRef.current = timestamp;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isStreaming]);

  return displayedText;
}
